/**
 * Store Configuration Schema
 *
 * Database schema for versioned store configuration. The seed data is located at
 * node/src/db/seed-data/store-configuration-relational.json
 *
 * @see node/src/features/store-configuration/SCHEMA.md for documentation
 */

import { createId } from "@paralleldrive/cuid2"
import { relations } from "drizzle-orm"
import {
  boolean,
  foreignKey,
  index,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  unique,
} from "drizzle-orm/pg-core"

// =============================================================================
// Configuration Versions
// =============================================================================

/**
 * Top-level configuration version container
 * Maps to storeConfiguration.version, lastUpdated, and globalRules.globalConstraints
 */
export const configurationVersions = pgTable(
  "configuration_versions",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    versionLabel: text("version_label").notNull(),
    lastUpdated: timestamp("last_updated", { withTimezone: true }).defaultNow().notNull(),
    isActive: boolean("is_active").default(false).notNull(),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    cloneOfVersionId: text("clone_of_version_id"),
    // Global constraints (from globalRules.globalConstraints)
    maxRowSpread: integer("max_row_spread"),
    globalConstraintsNotes: text("global_constraints_notes"),
    // Supported locales for translations (defaults to all available locales)
    supportedLocales: jsonb("supported_locales")
      .$type<string[]>()
      .default(["en", "cs", "de", "es", "fr", "hr", "it", "nl", "pl", "pt", "ro", "sk"]),
    // Data-driven gondola placement configuration (V2 algorithm)
    // When present, the optimizer uses the outside-in, weight-based algorithm
    // instead of abstract rules for gondola placement.
    gondolaPlacementConfig: jsonb("gondola_placement_config").$type<
      import("@/features/shelf-optimization/types/gondola-placement-config").GondolaPlacementConfig
    >(),
    // Backend planner config for groups / specialRoles / rowMixes / etc.
    // Mirrors the C# LayoutRulesDocument shape; sent verbatim as
    // `storeConfiguration.globalRules.layoutRules`. When null the backend
    // falls back to embedded seed defaults.
    layoutRules:
      jsonb("layout_rules").$type<
        import("@/features/shelf-optimization/types/layout-rules").LayoutRulesConfig
      >(),
    // Per-country/per-locale free-text hints shown to the user on the optimize
    // screen. Keys are either BCP-47 locales ("de-CH", "nl-NL") or ISO-3166
    // alpha-2 region codes ("CH", "NL"). Lookup falls back from full locale to
    // region.
    countryHints: jsonb("country_hints").$type<Record<string, string>>().default({}),
  },
  (table) => [
    foreignKey({
      columns: [table.cloneOfVersionId],
      foreignColumns: [table.id],
      name: "configuration_versions_clone_of_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("configuration_versions_is_active_idx").on(table.isActive),
    index("configuration_versions_created_at_idx").on(table.createdAt),
  ],
)

// =============================================================================
// Categories
// =============================================================================

/**
 * Categories (keyed by slug in JSON)
 * Maps to globalRules.categories.*
 */
export const configCategories = pgTable(
  "config_categories",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    slug: text().notNull(), // e.g., "stationaryHobby", "promotion"
    shortId: text("short_id").notNull(), // e.g., "SH", "PROMO"
    displayName: text("display_name").notNull(),
    color: text().notNull(),
    colorGroup: text("color_group"),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_categories_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_categories_version_slug_unique").on(table.configVersionId, table.slug),
    index("config_categories_version_idx").on(table.configVersionId),
  ],
)

/**
 * @deprecated Use configBandwidths instead - merchandise groups are now merged into bandwidths
 * This table is kept for migration purposes only
 */
export const configMerchandiseGroups = pgTable(
  "config_merchandise_groups",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    code: text().notNull(),
    displayName: text("display_name"),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_merchandise_groups_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_merchandise_groups_version_code_unique").on(table.configVersionId, table.code),
    index("config_merchandise_groups_version_idx").on(table.configVersionId),
  ],
)

// =============================================================================
// Bandwidths
// =============================================================================

/**
 * Placement type enum values
 */
export type PlacementType =
  | "wallOnly"
  | "gondolaOnly"
  | "split"
  | "splitWithMinimum"
  | "eitherOr"
  | "flexible"
  | "layout"

/**
 * Wall preference enum values
 */
export type WallPreference = "expand" | "maintain" | "compress" | "neutral"

/**
 * Bandwidth configuration per merchandise group
 * Maps to globalRules.bandwidths.*
 *
 * Note: This table now includes merchandise group data (code, displayName)
 * previously stored in config_merchandise_groups
 */
export const configBandwidths = pgTable(
  "config_bandwidths",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    // Merchandise group identity (merged from config_merchandise_groups)
    code: text().notNull(), // e.g., "MM", "PROMO", "PLASTIC"
    displayName: text("display_name"),
    // Alternative names for matching during Excel translation imports
    aliases: jsonb().$type<string[]>().default([]),
    // Ordering within the version (for UI display, separate from wallOrderPriority)
    sortOrder: integer("sort_order").notNull().default(0),
    // Placement type
    placementType: text("placement_type").$type<PlacementType>().notNull(),
    // Wall ranges
    wallMin: integer("wall_min").notNull(),
    wallMax: integer("wall_max").notNull(),
    wallBuying: integer("wall_buying"),
    // Gondola ranges
    gondolaMin: integer("gondola_min").notNull(),
    gondolaMax: integer("gondola_max").notNull(),
    gondolaBuying: integer("gondola_buying"),
    // Total buying meters (not split by wall/gondola) - defaults to max(wallBuying, gondolaBuying)
    buyingMeters: integer("buying_meters"),
    // Adjustment flags and priorities (legacy - kept for backwards compatibility)
    canIncrease: boolean("can_increase").default(false),
    increasePriority: integer("increase_priority"),
    canDecrease: boolean("can_decrease").default(false),
    decreasePriority: integer("decrease_priority"),
    // Wall-specific increase/decrease priorities (null = cannot adjust)
    wallIncreasePriority: integer("wall_increase_priority"),
    wallDecreasePriority: integer("wall_decrease_priority"),
    // Gondola-specific increase/decrease priorities (null = cannot adjust)
    gondolaIncreasePriority: integer("gondola_increase_priority"),
    gondolaDecreasePriority: integer("gondola_decrease_priority"),
    // Wall to gondola priority
    wallToGondolaPriority: integer("wall_to_gondola_priority"),
    minWallRequired: integer("min_wall_required"),
    // Wall preference and scaling
    wallPreference: text("wall_preference").$type<WallPreference>(),
    wallScalingSmall: numeric("wall_scaling_small"),
    wallScalingMedium: numeric("wall_scaling_medium"),
    wallScalingLarge: numeric("wall_scaling_large"),
    // Display
    color: text(),
    // When true, the Excel export generates an additional "(SMALL)" row after the regular (G)/(W) rows
    showSmall: boolean("show_small").default(false),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_bandwidths_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_bandwidths_version_code_unique").on(table.configVersionId, table.code),
    index("config_bandwidths_version_idx").on(table.configVersionId),
    index("config_bandwidths_sort_order_idx").on(table.configVersionId, table.sortOrder),
  ],
)

// =============================================================================
// Category-Bandwidth Mappings
// =============================================================================

/**
 * Join table for category -> bandwidths/merchandise groups (with ordering)
 * Maps to globalRules.categories.*.merchandiseGroups[]
 */
export const categoryMerchandiseGroups = pgTable(
  "category_merchandise_groups",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    categoryId: text("category_id").notNull(),
    bandwidthId: text("bandwidth_id").notNull(), // References configBandwidths
    position: integer().notNull(), // Order within the category's merchandiseGroups array
  },
  (table) => [
    foreignKey({
      columns: [table.categoryId],
      foreignColumns: [configCategories.id],
      name: "category_merchandise_groups_category_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "category_merchandise_groups_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("category_merchandise_groups_unique").on(table.categoryId, table.bandwidthId),
    index("category_merchandise_groups_category_idx").on(table.categoryId),
    index("category_merchandise_groups_position_idx").on(table.categoryId, table.position),
  ],
)

/**
 * Locale-specific overrides for bandwidth values
 * Maps to potential localeOverrides[] on bandwidths (future feature)
 */
export const bandwidthLocaleOverrides = pgTable(
  "bandwidth_locale_overrides",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    bandwidthId: text("bandwidth_id").notNull(),
    locale: text().notNull(), // e.g., "en-GB", "nl-NL"
    // Override values (all nullable - only set fields are overridden)
    wallMin: integer("wall_min"),
    wallMax: integer("wall_max"),
    gondolaMin: integer("gondola_min"),
    gondolaMax: integer("gondola_max"),
    buyingTotal: integer("buying_total"),
    buyingWall: integer("buying_wall"),
    buyingGondola: integer("buying_gondola"),
    canDecrease: boolean("can_decrease"),
    decreasePriority: integer("decrease_priority"),
    canIncrease: boolean("can_increase"),
    increasePriority: integer("increase_priority"),
    // Wall-specific increase/decrease priorities (null = no override)
    wallIncreasePriority: integer("wall_increase_priority"),
    wallDecreasePriority: integer("wall_decrease_priority"),
    // Gondola-specific increase/decrease priorities (null = no override)
    gondolaIncreasePriority: integer("gondola_increase_priority"),
    gondolaDecreasePriority: integer("gondola_decrease_priority"),
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "bandwidth_locale_overrides_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("bandwidth_locale_overrides_bandwidth_locale_unique").on(
      table.bandwidthId,
      table.locale,
    ),
    index("bandwidth_locale_overrides_bandwidth_idx").on(table.bandwidthId),
  ],
)

// =============================================================================
// Bandwidth Translations
// =============================================================================

/**
 * Locale-specific translations for bandwidth/merchandise group labels
 * Used for displaying localized names in blueprints and exports
 */
export const configBandwidthTranslations = pgTable(
  "config_bandwidth_translations",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    bandwidthId: text("bandwidth_id").notNull(),
    locale: text().notNull(), // e.g., "en", "de", "cs", "nl"
    // Short label for 3D blueprint rendering (space-limited)
    blueprintLabel: text("blueprint_label").notNull(),
    // Full display name for PDF/Excel exports
    displayName: text("display_name").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "config_bandwidth_translations_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_bandwidth_translations_bandwidth_locale_unique").on(
      table.bandwidthId,
      table.locale,
    ),
    index("config_bandwidth_translations_bandwidth_idx").on(table.bandwidthId),
    index("config_bandwidth_translations_locale_idx").on(table.locale),
  ],
)

// =============================================================================
// Fill Strategy
// =============================================================================

/**
 * Fill strategy configuration
 * Maps to globalRules.fillStrategy
 */
export const configFillStrategies = pgTable(
  "config_fill_strategies",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull().unique(),
    primaryStrategy: text("primary_strategy").notNull(), // e.g., "wallFirst"
    secondaryStrategy: text("secondary_strategy").notNull(), // e.g., "removeToGondolasInPriority"
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_fill_strategies_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
)

// =============================================================================
// Wall Order Priority
// =============================================================================

/**
 * Wall order priority (ordered list of merchandise group codes)
 * Maps to globalRules.wallOrderPriority[]
 */
export const configWallOrderPriority = pgTable(
  "config_wall_order_priority",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    bandwidthId: text("bandwidth_id").notNull(), // References configBandwidths
    position: integer().notNull(), // Order in the array
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_wall_order_priority_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "config_wall_order_priority_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_wall_order_priority_version_bandwidth_unique").on(
      table.configVersionId,
      table.bandwidthId,
    ),
    index("config_wall_order_priority_version_position_idx").on(
      table.configVersionId,
      table.position,
    ),
  ],
)

// =============================================================================
// Category Sort Order
// =============================================================================

/**
 * Category sort order (ordered list of merchandise group codes)
 * Controls display order in the Design Blueprint sidebar and Excel exports.
 * Maps to globalRules.categorySortOrder[]
 */
export const configCategorySortOrder = pgTable(
  "config_category_sort_order",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    bandwidthId: text("bandwidth_id").notNull(), // References configBandwidths
    position: integer().notNull(), // Order in the array
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_category_sort_order_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "config_category_sort_order_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_category_sort_order_version_bandwidth_unique").on(
      table.configVersionId,
      table.bandwidthId,
    ),
    index("config_category_sort_order_version_position_idx").on(
      table.configVersionId,
      table.position,
    ),
  ],
)

// =============================================================================
// Gondola Arrangements
// =============================================================================

/**
 * Gondola arrangement container (grouped by category)
 * Maps to globalRules.gondolaArrangements[]
 */
export const configGondolaArrangements = pgTable(
  "config_gondola_arrangements",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    categorySlug: text("category_slug").notNull(), // References category by slug
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_gondola_arrangements_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("config_gondola_arrangements_version_category_unique").on(
      table.configVersionId,
      table.categorySlug,
    ),
    index("config_gondola_arrangements_version_idx").on(table.configVersionId),
  ],
)

/**
 * Individual arrangement patterns within a category
 * Maps to globalRules.gondolaArrangements[].arrangements[]
 */
export const gondolaArrangementPatterns = pgTable(
  "gondola_arrangement_patterns",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    arrangementId: text("arrangement_id").notNull(),
    priority: integer().notNull(),
    notes: text(),
    position: integer().notNull(), // Order within the category's arrangements
  },
  (table) => [
    foreignKey({
      columns: [table.arrangementId],
      foreignColumns: [configGondolaArrangements.id],
      name: "gondola_arrangement_patterns_arrangement_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("gondola_arrangement_patterns_arrangement_idx").on(table.arrangementId),
    index("gondola_arrangement_patterns_position_idx").on(table.arrangementId, table.position),
  ],
)

/**
 * Merchandise group positions within a pattern
 * Maps to globalRules.gondolaArrangements[].arrangements[].positions[]
 */
export const gondolaPatternPositions = pgTable(
  "gondola_pattern_positions",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    patternId: text("pattern_id").notNull(),
    merchandiseGroupCode: text("merchandise_group_code").notNull(), // Store code directly for flexibility
    position: integer().notNull(), // Order in the positions array
  },
  (table) => [
    foreignKey({
      columns: [table.patternId],
      foreignColumns: [gondolaArrangementPatterns.id],
      name: "gondola_pattern_positions_pattern_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("gondola_pattern_positions_pattern_idx").on(table.patternId),
    index("gondola_pattern_positions_position_idx").on(table.patternId, table.position),
  ],
)

// =============================================================================
// Abstract Rules
// =============================================================================

/**
 * Abstract rule type enum values
 */
export type AbstractRuleType =
  | "opposite"
  | "adjacent"
  | "near"
  | "separated"
  | "backToBack"
  | "grouping"
  | "sequential"

/**
 * Preferred relation for grouping rules
 */
export type PreferredRelation = "adjacent" | "opposite" | "near" | "backToBack"

/**
 * Abstract rules for merchandise group relationships
 * Maps to globalRules.abstractRules[]
 */
export const configAbstractRules = pgTable(
  "config_abstract_rules",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    type: text().$type<AbstractRuleType>().notNull(),
    priority: numeric().notNull(), // 1-20, higher = more important
    preferredRelation: text("preferred_relation").$type<PreferredRelation>(),
    notes: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_abstract_rules_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("config_abstract_rules_version_idx").on(table.configVersionId),
    index("config_abstract_rules_type_idx").on(table.type),
  ],
)

/**
 * Group level enum for abstract/prohibited rules
 */
export type RuleGroupLevel = "group1" | "group2" | "group3"

/**
 * Merchandise groups within abstract rules (group1, group2, group3)
 * Maps to globalRules.abstractRules[].group1/group2/group3[]
 */
export const abstractRuleGroups = pgTable(
  "abstract_rule_groups",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    ruleId: text("rule_id").notNull(),
    groupLevel: text("group_level").$type<RuleGroupLevel>().notNull(),
    merchandiseGroupCode: text("merchandise_group_code").notNull(),
    position: integer().notNull(), // Order within the group array
  },
  (table) => [
    foreignKey({
      columns: [table.ruleId],
      foreignColumns: [configAbstractRules.id],
      name: "abstract_rule_groups_rule_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("abstract_rule_groups_rule_idx").on(table.ruleId),
    index("abstract_rule_groups_rule_level_idx").on(table.ruleId, table.groupLevel, table.position),
  ],
)

// =============================================================================
// Prohibited Rules
// =============================================================================

/**
 * Prohibited placement type enum values
 */
export type ProhibitedPlacementType = "adjacent" | "opposite" | "backToBack" | "near"

/**
 * Prohibited rules for hard constraints
 * Maps to globalRules.prohibitedRules[]
 */
export const configProhibitedRules = pgTable(
  "config_prohibited_rules",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    priority: integer().notNull(),
    reason: text(),
    notes: text(),
    // Types stored as JSONB array of strings
    types: jsonb().$type<ProhibitedPlacementType[]>(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_prohibited_rules_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("config_prohibited_rules_version_idx").on(table.configVersionId),
  ],
)

/**
 * Merchandise groups within prohibited rules (group1, group2)
 * Maps to globalRules.prohibitedRules[].group1/group2[]
 */
export const prohibitedRuleGroups = pgTable(
  "prohibited_rule_groups",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    ruleId: text("rule_id").notNull(),
    groupLevel: text("group_level").$type<"group1" | "group2">().notNull(),
    merchandiseGroupCode: text("merchandise_group_code").notNull(),
    position: integer().notNull(), // Order within the group array
  },
  (table) => [
    foreignKey({
      columns: [table.ruleId],
      foreignColumns: [configProhibitedRules.id],
      name: "prohibited_rule_groups_rule_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("prohibited_rule_groups_rule_idx").on(table.ruleId),
    index("prohibited_rule_groups_rule_level_idx").on(
      table.ruleId,
      table.groupLevel,
      table.position,
    ),
  ],
)

// =============================================================================
// Drizzle Relations (for relational query API)
// =============================================================================

export const configurationVersionsRelations = relations(configurationVersions, ({ many, one }) => ({
  categories: many(configCategories),
  bandwidths: many(configBandwidths),
  fillStrategies: many(configFillStrategies),
  wallOrderPriority: many(configWallOrderPriority),
  categorySortOrder: many(configCategorySortOrder),
  gondolaArrangements: many(configGondolaArrangements),
  abstractRules: many(configAbstractRules),
  prohibitedRules: many(configProhibitedRules),
  aisleRules: many(configAisleRules),
  cloneOf: one(configurationVersions, {
    fields: [configurationVersions.cloneOfVersionId],
    references: [configurationVersions.id],
  }),
}))

export const configCategoriesRelations = relations(configCategories, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [configCategories.configVersionId],
    references: [configurationVersions.id],
  }),
  merchandiseGroupMappings: many(categoryMerchandiseGroups),
}))

/**
 * @deprecated - configMerchandiseGroups is deprecated
 */
export const configMerchandiseGroupsRelations = relations(configMerchandiseGroups, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [configMerchandiseGroups.configVersionId],
    references: [configurationVersions.id],
  }),
}))

export const categoryMerchandiseGroupsRelations = relations(
  categoryMerchandiseGroups,
  ({ one }) => ({
    category: one(configCategories, {
      fields: [categoryMerchandiseGroups.categoryId],
      references: [configCategories.id],
    }),
    bandwidth: one(configBandwidths, {
      fields: [categoryMerchandiseGroups.bandwidthId],
      references: [configBandwidths.id],
    }),
  }),
)

export const configBandwidthsRelations = relations(configBandwidths, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [configBandwidths.configVersionId],
    references: [configurationVersions.id],
  }),
  categoryMappings: many(categoryMerchandiseGroups),
  wallOrderPriority: many(configWallOrderPriority),
  categorySortOrder: many(configCategorySortOrder),
  localeOverrides: many(bandwidthLocaleOverrides),
  translations: many(configBandwidthTranslations),
}))

export const configBandwidthTranslationsRelations = relations(
  configBandwidthTranslations,
  ({ one }) => ({
    bandwidth: one(configBandwidths, {
      fields: [configBandwidthTranslations.bandwidthId],
      references: [configBandwidths.id],
    }),
  }),
)

export const bandwidthLocaleOverridesRelations = relations(bandwidthLocaleOverrides, ({ one }) => ({
  bandwidth: one(configBandwidths, {
    fields: [bandwidthLocaleOverrides.bandwidthId],
    references: [configBandwidths.id],
  }),
}))

export const configFillStrategiesRelations = relations(configFillStrategies, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [configFillStrategies.configVersionId],
    references: [configurationVersions.id],
  }),
}))

export const configWallOrderPriorityRelations = relations(configWallOrderPriority, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [configWallOrderPriority.configVersionId],
    references: [configurationVersions.id],
  }),
  bandwidth: one(configBandwidths, {
    fields: [configWallOrderPriority.bandwidthId],
    references: [configBandwidths.id],
  }),
}))

export const configCategorySortOrderRelations = relations(configCategorySortOrder, ({ one }) => ({
  configVersion: one(configurationVersions, {
    fields: [configCategorySortOrder.configVersionId],
    references: [configurationVersions.id],
  }),
  bandwidth: one(configBandwidths, {
    fields: [configCategorySortOrder.bandwidthId],
    references: [configBandwidths.id],
  }),
}))

export const configGondolaArrangementsRelations = relations(
  configGondolaArrangements,
  ({ one, many }) => ({
    configVersion: one(configurationVersions, {
      fields: [configGondolaArrangements.configVersionId],
      references: [configurationVersions.id],
    }),
    patterns: many(gondolaArrangementPatterns),
  }),
)

export const gondolaArrangementPatternsRelations = relations(
  gondolaArrangementPatterns,
  ({ one, many }) => ({
    arrangement: one(configGondolaArrangements, {
      fields: [gondolaArrangementPatterns.arrangementId],
      references: [configGondolaArrangements.id],
    }),
    positions: many(gondolaPatternPositions),
  }),
)

export const gondolaPatternPositionsRelations = relations(gondolaPatternPositions, ({ one }) => ({
  pattern: one(gondolaArrangementPatterns, {
    fields: [gondolaPatternPositions.patternId],
    references: [gondolaArrangementPatterns.id],
  }),
}))

export const configAbstractRulesRelations = relations(configAbstractRules, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [configAbstractRules.configVersionId],
    references: [configurationVersions.id],
  }),
  groups: many(abstractRuleGroups),
}))

export const abstractRuleGroupsRelations = relations(abstractRuleGroups, ({ one }) => ({
  rule: one(configAbstractRules, {
    fields: [abstractRuleGroups.ruleId],
    references: [configAbstractRules.id],
  }),
}))

export const configProhibitedRulesRelations = relations(configProhibitedRules, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [configProhibitedRules.configVersionId],
    references: [configurationVersions.id],
  }),
  groups: many(prohibitedRuleGroups),
}))

export const prohibitedRuleGroupsRelations = relations(prohibitedRuleGroups, ({ one }) => ({
  rule: one(configProhibitedRules, {
    fields: [prohibitedRuleGroups.ruleId],
    references: [configProhibitedRules.id],
  }),
}))

// =============================================================================
// Aisle Rules (visual gondola placement configuration)
// =============================================================================

/**
 * Aisle rule relation type enum values
 * 'anchor' is implicit for wall sections (position 0)
 */
export type AisleRelationType =
  | "oppositeWall"
  | "backToBack"
  | "oppositeGondola"
  | "adjacent"
  | "t-junction"
  | "other"

/**
 * Placement modifier for anchor filtering
 */
export type PlacementModifier = "all" | "wall" | "gondola"

/**
 * Aisle rules - top-level container for aisle definitions
 * Maps to GondolaArrangementList in the algorithm
 */
export const configAisleRules = pgTable(
  "config_aisle_rules",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    name: text().notNull(), // e.g., "Stationery & Deco aisle"
    priority: integer().notNull(), // Lower = higher priority (A1, A2, etc.)
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "config_aisle_rules_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("config_aisle_rules_version_idx").on(table.configVersionId),
    index("config_aisle_rules_priority_idx").on(table.configVersionId, table.priority),
  ],
)

/**
 * Aisle sections - WALL anchor or GONDOLA sections within an aisle
 * Maps to GondolaArrangementMemberGroup in the algorithm
 */
export const configAisleSections = pgTable(
  "config_aisle_sections",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    aisleId: text("aisle_id").notNull(),
    sectionType: text("section_type").$type<"wall" | "gondola">().notNull(),
    position: integer().notNull(), // Order within aisle (0 = wall/anchor, 1+ = gondolas)
    relationToPrevious: text("relation_to_previous").$type<AisleRelationType>(), // null for wall sections
  },
  (table) => [
    foreignKey({
      columns: [table.aisleId],
      foreignColumns: [configAisleRules.id],
      name: "config_aisle_sections_aisle_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("config_aisle_sections_aisle_idx").on(table.aisleId),
    index("config_aisle_sections_position_idx").on(table.aisleId, table.position),
  ],
)

/**
 * MGs within an aisle section
 * Maps to members[] in GondolaArrangementMemberGroup
 */
export const configAisleSectionMgs = pgTable(
  "config_aisle_section_mgs",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    sectionId: text("section_id").notNull(),
    mgCode: text("mg_code").notNull(), // Merchandise group code
    priority: integer().notNull(), // Fill order within section (lower = higher priority)
    placementModifier: text("placement_modifier").$type<PlacementModifier>().default("all"), // For anchor filtering
  },
  (table) => [
    foreignKey({
      columns: [table.sectionId],
      foreignColumns: [configAisleSections.id],
      name: "config_aisle_section_mgs_section_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("config_aisle_section_mgs_section_idx").on(table.sectionId),
    index("config_aisle_section_mgs_priority_idx").on(table.sectionId, table.priority),
  ],
)

// Aisle rules relations
export const configAisleRulesRelations = relations(configAisleRules, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [configAisleRules.configVersionId],
    references: [configurationVersions.id],
  }),
  sections: many(configAisleSections),
}))

export const configAisleSectionsRelations = relations(configAisleSections, ({ one, many }) => ({
  aisle: one(configAisleRules, {
    fields: [configAisleSections.aisleId],
    references: [configAisleRules.id],
  }),
  merchandiseGroups: many(configAisleSectionMgs),
}))

export const configAisleSectionMgsRelations = relations(configAisleSectionMgs, ({ one }) => ({
  section: one(configAisleSections, {
    fields: [configAisleSectionMgs.sectionId],
    references: [configAisleSections.id],
  }),
}))
